import {BaseModel} from '@shared/models/base.model';
import {FileType, MetadataType} from '@generated/graphql';
import {User} from '@app/modules/user/models/user.model';
import {FileType as FileTypeModel} from './file-type.model';
import {fileTypeFactory} from "@modules/file/factories/file-type.factory";
import {userFactory} from "@modules/user/factories/user.factory";
import {basename, dirname} from "@shared/helpers/file.helpers";

export class File extends BaseModel implements FileType {
  static imageMimeTypes: string[] = [
    // Images
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
  ]

  static videoMimeTypes: string[] = [
    'video/mp4',
    'video/x-msvideo',
    'video/x-matroska',
    'video/webm',
    'video/ogg',
  ]

  static documentMimeTypes: string[] = [
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]

  static validMimeTypes: string[] = [
    // Images
    ...File.imageMimeTypes,
    // Video
    ...File.videoMimeTypes,
    // Documents
    ...File.documentMimeTypes
  ];

  static mimeTypesWithIcon: { types: string[], icon: string }[] = [
    {
      types: ['application/pdf'],
      icon: 'picture_as_pdf'
    },
    {
      types: File.documentMimeTypes,
      icon: 'description'
    },
    {
      types: File.imageMimeTypes,
      icon: 'image'
    },
    {
      types: File.videoMimeTypes,
      icon: 'movie'
    }
  ]

  static readonly defaultExtensions: string[] = [
    '.jpeg',
    '.png',
    '.jpg',
    '.gif',
    '.svg',
    '.xlsx',
    '.doc',
    '.docx',
    '.ppt',
    '.pptx',
    '.ods',
    '.odt',
    '.odp',
    '.pdf',
    '.csv',
    '.excel'
  ];

  id: string;
  createdBy: User;
  fileTypeId: string;
  fileType: FileTypeModel;
  hasFileId: string;
  hasFileType: string;
  metadata: MetadataType;
  mimeType: string;
  size: number;
  name: string;
  extension?: string;
  url: string;
  visibility: boolean;
  clientVisible: boolean;
  clientEditable: boolean;
  notifyExpertOnNewDocument: boolean;
  notifyClientOnNewDocument: boolean;
  downloadUrl: string;
  path: string;
  isDirectory: boolean;
  isAutoGenerated: boolean;
  _model: string;

  constructor(state: FileType) {
    super(state);

    this.fileType = fileTypeFactory.tryCreating(state.fileType)

    this.createdBy = userFactory.tryCreating(state.createdBy)
  }

  get label() {
    return this.isDirectory ? basename(dirname(this.path)) : this.metadata.originalName.substring(0, this.metadata.originalName.lastIndexOf('.'))
  }

  get dirname() {
    return dirname(this.path)
  }

  static validFiles(files: Blob[]): Blob[] {
    return files.filter(file => !File.validFile(file));
  }


  static validFile(file: Blob): boolean {
    return !File.validMimeTypes.includes(file.type);
  }

  getModelName(): string {
    return File.name;
  }

  /**
   * Whether this file/folder can be moved to the given destination directory.
   *
   * Rules:
   * - Structural:
   *   - Destination must be a directory.
   *   - A directory cannot be moved into itself or one of its own sub‑directories.
   * - Permissions (aligned with canProcessDocument$/canManage):
   *   - Experts (isExpert=true) can move anywhere that is structurally valid.
   *   - Non‑experts can only move into destinations that are client‑editable
   *     (destination.clientEditable truthy). System folders are thus
   *     automatically excluded, since their clientEditable is always false.
   */
  canMoveTo(destination: File, isExpert: boolean = false) {
    if (!destination?.isDirectory) {
      return false
    }

    const structurallyAllowed = this.isDirectory
      ? destination.id !== this.id && dirname(dirname(this.path)) !== dirname(destination.path)
      : true

    if (!structurallyAllowed) {
      return false
    }

    // Experts can move anywhere that passes structural checks
    if (isExpert) {
      return true
    }

    // Non‑experts: destination must explicitly allow client edit rights
    const destinationAllowsClientEdit = !!destination.clientEditable

    return destinationAllowsClientEdit
  }
}
